/**
 * a4InvoiceTemplate — pure function: ReceiptData -> standalone HTML
 * suitable for Chromium's printToPDF (A4, print-background on).
 *
 * This is the document a GST-registered buyer files their input credit
 * against, so it carries the particulars Rule 46 of the CGST Rules asks
 * for and the thermal receipt has no room to print:
 *
 *   • both parties' GSTIN, address and state (with code)
 *   • place of supply, and whether tax is payable on reverse charge
 *   • per-line taxable value, GST rate and tax charged
 *   • a rate-wise tax summary grouped by HSN
 *   • the total in words, and an authorised-signatory block
 *
 * Every block is data-driven: a walk-in cash sale with no customer record
 * still prints, minus the buyer's GST particulars. Nothing here is behind a
 * setting, because a tax invoice is not a preference.
 *
 * Self-contained: inlines its CSS, uses no external assets, and avoids
 * JavaScript so the PDF window can disable the JS context entirely.
 */

import type { ReceiptData, ReceiptTaxSummaryRow } from '../contracts/ReceiptContracts';
import { amountInWords, escapeHtml, inr, num, stateWithCode } from './invoiceFormat';

export function buildA4InvoiceHtml(data: ReceiptData): string {
  const interState = data.isInterState === true;
  const summary: ReceiptTaxSummaryRow[] = data.taxSummary ?? [];
  const hasCess = summary.some((r) => r.cess > 0);

  const itemRows = data.items
    .map((it, i) => `
      <tr>
        <td class="num">${i + 1}</td>
        <td>${escapeHtml(it.name)}${it.hsn ? `<div class="hsn">HSN ${escapeHtml(it.hsn)}</div>` : ''}</td>
        <td class="num">${it.qty.toFixed(2)}${it.unit ? ` <span class="unit">${escapeHtml(it.unit)}</span>` : ''}</td>
        <td class="num">${num(it.rate)}</td>
        <td class="num">${it.discountPct ? it.discountPct.toFixed(1) + '%' : '—'}</td>
        <td class="num">${num(it.taxableValue ?? it.amount)}</td>
        <td class="num">${typeof it.gstRate === 'number' ? it.gstRate + '%' : '—'}</td>
        <td class="num">${typeof it.taxAmount === 'number' ? num(it.taxAmount) : '—'}</td>
        <td class="num">${num(it.amount)}</td>
      </tr>
    `)
    .join('');

  // Rate-wise summary. Intra-state splits into CGST + SGST, inter-state is a
  // single IGST column — printing both would leave an empty pair on every bill.
  const summaryRows = summary
    .map((r) => `
      <tr>
        <td>${escapeHtml(r.hsn || '—')}</td>
        <td class="num">${r.gstRate}%</td>
        <td class="num">${num(r.taxable)}</td>
        ${interState
          ? `<td class="num">${num(r.igst)}</td>`
          : `<td class="num">${num(r.cgst)}</td><td class="num">${num(r.sgst)}</td>`}
        ${hasCess ? `<td class="num">${num(r.cess)}</td>` : ''}
        <td class="num">${num(r.cgst + r.sgst + r.igst + r.cess)}</td>
      </tr>
    `)
    .join('');

  const summaryTotals = summary.reduce(
    (acc, r) => ({
      taxable: acc.taxable + r.taxable,
      cgst: acc.cgst + r.cgst,
      sgst: acc.sgst + r.sgst,
      igst: acc.igst + r.igst,
      cess: acc.cess + r.cess,
    }),
    { taxable: 0, cgst: 0, sgst: 0, igst: 0, cess: 0 },
  );
  const totalTax = summaryTotals.cgst + summaryTotals.sgst + summaryTotals.igst + summaryTotals.cess;

  const taxSummaryBlock = summary.length
    ? `
  <div class="section-label">Tax summary</div>
  <table class="lines tax-summary">
    <thead>
      <tr>
        <th>HSN / SAC</th>
        <th class="num">Rate</th>
        <th class="num">Taxable value</th>
        ${interState
          ? '<th class="num">IGST</th>'
          : '<th class="num">CGST</th><th class="num">SGST</th>'}
        ${hasCess ? '<th class="num">Cess</th>' : ''}
        <th class="num">Total tax</th>
      </tr>
    </thead>
    <tbody>${summaryRows}</tbody>
    <tfoot>
      <tr>
        <td colspan="2">Total</td>
        <td class="num">${num(summaryTotals.taxable)}</td>
        ${interState
          ? `<td class="num">${num(summaryTotals.igst)}</td>`
          : `<td class="num">${num(summaryTotals.cgst)}</td><td class="num">${num(summaryTotals.sgst)}</td>`}
        ${hasCess ? `<td class="num">${num(summaryTotals.cess)}</td>` : ''}
        <td class="num">${num(totalTax)}</td>
      </tr>
    </tfoot>
  </table>`
    : '';

  const taxRows = data.taxes
    .map((t) => `<tr><td>${escapeHtml(t.label)}</td><td class="num">${inr(t.amount)}</td></tr>`)
    .join('');

  const dueLine =
    typeof data.dueAmount === 'number'
      ? `<strong>Balance due: ${inr(data.dueAmount)}</strong>${data.dueDate ? ` (by ${escapeHtml(data.dueDate)})` : ''}`
      : '';

  const paymentBlock = data.payment
    ? `<div class="pay">Paid via ${escapeHtml(data.payment.method)}: ${inr(data.payment.paid)}${
        typeof data.payment.change === 'number' ? `<br/>Change: ${inr(data.payment.change)}` : ''
      }${dueLine ? `<br/>${dueLine}` : ''}</div>`
    : dueLine
      ? `<div class="pay">${dueLine}</div>`
      : '';

  const buyer = data.customer;
  const buyerBlock = buyer
    ? `
      <div class="party">
        <span class="col-label">Bill to</span>
        <div class="party-name">${escapeHtml(buyer.name)}</div>
        ${buyer.address ? `<div>${escapeHtml(buyer.address)}</div>` : ''}
        ${buyer.gstin ? `<div><strong>GSTIN: ${escapeHtml(buyer.gstin)}</strong></div>` : ''}
        ${buyer.stateName || buyer.stateCode
          ? `<div>State: ${escapeHtml(stateWithCode(buyer.stateName, buyer.stateCode))}</div>`
          : ''}
        ${buyer.phone ? `<div>Ph: ${escapeHtml(buyer.phone)}</div>` : ''}
      </div>`
    : `
      <div class="party">
        <span class="col-label">Bill to</span>
        <div class="party-name">${escapeHtml(data.customerName || 'Walk-in customer')}</div>
      </div>`;

  const supplyBlock = `
      <div class="party supply">
        <span class="col-label">Place of supply</span>
        <div class="party-name">${escapeHtml(
          stateWithCode(data.placeOfSupply, data.placeOfSupplyCode) || '—',
        )}</div>
        <div>Supply: ${interState ? 'Inter-state (IGST)' : 'Intra-state (CGST + SGST)'}</div>
        <div>Reverse charge: ${data.reverseCharge ? 'Yes' : 'No'}</div>
      </div>`;

  const notesBlock =
    data.terms || data.notes
      ? `<section class="notes">
        ${data.terms ? `<div><span class="col-label">Terms &amp; conditions</span><div style="white-space:pre-line">${escapeHtml(data.terms)}</div></div>` : ''}
        ${data.notes ? `<div><span class="col-label">Notes</span><div>${escapeHtml(data.notes)}</div></div>` : ''}
      </section>`
      : '';

  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(data.invoiceNumber)}</title>
<style>
  @page { size: A4; margin: 14mm 12mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; font-size: 10.5pt; color: #1a1a1a; margin: 0; }
  h1, h2, h3 { margin: 0; font-weight: 600; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 2px solid #1a1a1a; }
  .head .seller { line-height: 1.45; }
  .store-name { font-size: 17pt; font-weight: 600; }
  .meta { text-align: right; font-size: 10pt; color: #444; line-height: 1.5; }
  .meta .label { color: #888; text-transform: uppercase; letter-spacing: 0.06em; font-size: 9pt; font-weight: 600; }
  .meta .num { font-size: 14pt; color: #1a1a1a; font-weight: 600; }
  .parties { display: flex; gap: 14px; margin: 14px 0 16px; font-size: 10pt; color: #333; }
  .party { flex: 1; line-height: 1.45; border: 1px solid #ddd; border-radius: 6px; padding: 9px 11px; }
  .party.supply { flex: 0 0 38%; background: #fafafb; }
  .party-name { font-weight: 600; font-size: 11pt; color: #1a1a1a; }
  .col-label { display: block; color: #888; text-transform: uppercase; letter-spacing: 0.05em; font-size: 8pt; margin-bottom: 3px; }
  table.lines { width: 100%; border-collapse: collapse; }
  table.lines th { text-align: left; font-weight: 600; font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.04em; color: #666; padding: 6px; border-bottom: 1px solid #ccc; }
  table.lines td { padding: 7px 6px; border-bottom: 1px solid #eee; vertical-align: top; font-size: 10pt; }
  table.lines td.num, table.lines th.num { text-align: right; font-variant-numeric: tabular-nums; }
  table.lines tfoot td { font-weight: 600; border-top: 1px solid #ccc; border-bottom: none; }
  .unit { color: #888; font-size: 9pt; }
  .hsn { color: #888; font-size: 8.5pt; margin-top: 1px; text-transform: uppercase; letter-spacing: 0.03em; }
  .section-label { margin-top: 20px; margin-bottom: 6px; color: #888; text-transform: uppercase; letter-spacing: 0.05em; font-size: 8pt; font-weight: 600; }
  .totals { margin-top: 18px; display: flex; justify-content: flex-end; }
  table.tot { width: 48%; border-collapse: collapse; font-size: 10.5pt; }
  table.tot td { padding: 4px 8px; }
  table.tot td.num { text-align: right; font-variant-numeric: tabular-nums; }
  table.tot tr.grand td { padding-top: 10px; border-top: 2px solid #1a1a1a; font-weight: 700; font-size: 13pt; }
  .words { margin-top: 10px; padding: 9px 11px; border: 1px solid #ddd; border-radius: 6px; font-size: 10pt; }
  .pay { margin-top: 12px; padding: 10px 12px; background: #f4f4f6; border-radius: 6px; font-size: 10pt; color: #333; line-height: 1.5; }
  .notes { margin-top: 14px; display: flex; gap: 20px; font-size: 9.5pt; color: #444; line-height: 1.45; }
  .notes > div { flex: 1; }
  .sign { margin-top: 28px; display: flex; justify-content: space-between; align-items: flex-end; font-size: 9.5pt; color: #444; }
  .sign .box { text-align: right; }
  .sign .rule { margin-top: 40px; border-top: 1px solid #999; padding-top: 4px; min-width: 190px; }
  footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #eee; text-align: center; color: #888; font-size: 9pt; }
</style>
</head>
<body>
  <header class="head">
    <div class="seller">
      <div class="store-name">${escapeHtml(data.storeName)}</div>
      ${data.addressLine ? `<div>${escapeHtml(data.addressLine)}</div>` : ''}
      ${data.gstin ? `<div><strong>GSTIN: ${escapeHtml(data.gstin)}</strong></div>` : ''}
      ${data.stateName || data.stateCode
        ? `<div>State: ${escapeHtml(stateWithCode(data.stateName, data.stateCode))}</div>`
        : ''}
      ${data.phone ? `<div>Ph: ${escapeHtml(data.phone)}</div>` : ''}
    </div>
    <div class="meta">
      <div class="label">Tax invoice</div>
      <div class="num">${escapeHtml(data.invoiceNumber)}</div>
      <div>${escapeHtml(data.invoiceDate)}</div>
    </div>
  </header>

  <section class="parties">
    ${buyerBlock}
    ${supplyBlock}
  </section>

  <table class="lines">
    <thead>
      <tr>
        <th class="num">#</th>
        <th>Item</th>
        <th class="num">Qty</th>
        <th class="num">Rate</th>
        <th class="num">Disc</th>
        <th class="num">Taxable</th>
        <th class="num">GST</th>
        <th class="num">Tax</th>
        <th class="num">Amount</th>
      </tr>
    </thead>
    <tbody>${itemRows}</tbody>
  </table>

  ${taxSummaryBlock}

  <div class="totals">
    <table class="tot">
      <tr><td>Subtotal</td><td class="num">${inr(data.subtotal)}</td></tr>
      ${data.discount ? `<tr><td>Discount</td><td class="num">- ${inr(data.discount)}</td></tr>` : ''}
      ${taxRows}
      ${typeof data.roundOff === 'number'
        ? `<tr><td>Round off</td><td class="num">${data.roundOff < 0 ? '- ' : '+ '}${inr(Math.abs(data.roundOff))}</td></tr>`
        : ''}
      <tr class="grand"><td>Total</td><td class="num">${inr(data.total)}</td></tr>
    </table>
  </div>

  <div class="words">
    <span class="col-label">Amount in words</span>
    <div>${escapeHtml(amountInWords(data.total))}</div>
  </div>

  ${paymentBlock}
  ${notesBlock}

  <section class="sign">
    <div>E. &amp; O.E. — Certified that the particulars above are true and correct.</div>
    <div class="box">
      <div>For ${escapeHtml(data.storeName)}</div>
      <div class="rule">Authorised signatory</div>
    </div>
  </section>

  <footer>
    <div>${escapeHtml(data.footerLine1 || 'Thank you! Visit again.')}</div>
    <div>${escapeHtml(data.footerLine2 || 'Powered by BigxBook')}</div>
  </footer>
</body>
</html>`;
}
